import { Component } from '@theme/component';

/**
 * Language Selector Drawer Component
 * Handles country and language selection with a two-step flow (main view -> selection list)
 */
class LanguageSelectorDrawer extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.drawerId = this.dataset.drawerId || 'language-selector';
    this.selectedCountry = this.refs.countryInput.value;
    this.selectedLanguage = this.refs.languageInput.value;
    this.hasChanges = false;

    document.addEventListener('open-language-drawer', this.handleOpen.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('open-language-drawer', this.handleOpen.bind(this));
  }

  handleOpen(event) {
    if (!event.detail?.drawerId || event.detail.drawerId === this.drawerId) {
      this.triggerElement = event.detail?.triggerElement || document.activeElement;
      this.open();
    }
  }

  open() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    document.body.style.overflow = 'hidden';
    dialog.showModal();

    this.setupEventListeners();
  }

  close() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    document.body.style.overflow = '';
    dialog.close();

    this.removeEventListeners();

    if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
      this.triggerElement.focus();
    }
  }

  openCountryList() {
    this.refs.countryOverlay.removeAttribute('hidden');
    setTimeout(() => {
      this.refs.countrySearch?.focus();
    }, 100);
  }

  closeCountryList() {
    this.refs.countryOverlay.setAttribute('hidden', '');
    this.refs.countrySearch.value = '';
    this.filterCountries();
  }

  openLanguageList() {
    this.refs.languageOverlay.removeAttribute('hidden');
  }

  closeLanguageList() {
    this.refs.languageOverlay.setAttribute('hidden', '');
  }

  selectCountry(event) {
    const button = event.target.closest('.language-drawer__list-item');
    if (!button) return;

    const countryCode = button.dataset.countryCode;
    const countryName = button.dataset.countryName;
    const currencySymbol = button.dataset.currencySymbol;

    this.selectedCountry = countryCode;
    this.refs.countryInput.value = countryCode;
    this.refs.selectedCountryText.textContent = `${countryName} (${currencySymbol})`;

    // Update flag
    const flagElement = this.refs.countryFlag;
    if (flagElement) {
      flagElement.className = `language-drawer__flag ${countryCode.toLowerCase()}`;
    }

    // Update active state
    this.updateActiveStates('country', countryCode);

    this.hasChanges = true;
    this.closeCountryList();
  }

  selectLanguage(event) {
    const button = event.target.closest('.language-drawer__list-item');
    if (!button) return;

    const languageCode = button.dataset.languageCode;
    const languageName = button.dataset.languageName;

    this.selectedLanguage = languageCode;
    this.refs.languageInput.value = languageCode;
    this.refs.selectedLanguageText.textContent = languageName;

    // Update active state
    this.updateActiveStates('language', languageCode);

    this.hasChanges = true;
    this.closeLanguageList();
  }

  updateActiveStates(type, code) {
    const dataAttr = type === 'country' ? 'countryCode' : 'languageCode';
    const overlay = type === 'country' ? this.refs.countryOverlay : this.refs.languageOverlay;

    overlay.querySelectorAll('.language-drawer__list-item').forEach(item => {
      if (item.dataset[dataAttr] === code) {
        item.classList.add('language-drawer__list-item--active');

        // Add checkmark if not exists
        if (!item.querySelector('.language-drawer__checkmark')) {
          const checkmark = document.createElement('span');
          checkmark.className = 'language-drawer__checkmark';
          checkmark.innerHTML = '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></span>';
          item.appendChild(checkmark);
        }
      } else {
        item.classList.remove('language-drawer__list-item--active');
        const checkmark = item.querySelector('.language-drawer__checkmark');
        if (checkmark) checkmark.remove();
      }
    });
  }

  filterCountries() {
    const searchTerm = this.refs.countrySearch.value.toLowerCase();
    const items = this.refs.countryOverlay.querySelectorAll('.language-drawer__list-item');

    items.forEach(item => {
      const text = item.querySelector('.language-drawer__list-item-text')?.textContent.toLowerCase() || '';
      if (text.includes(searchTerm)) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', '');
      }
    });
  }

  saveSelection() {
    if (this.hasChanges) {
      const form = this.refs.form;
      if (form) {
        form.submit();
      }
    } else {
      this.close();
    }
  }

  setupEventListeners() {
    const { dialog } = this.refs;
    dialog.addEventListener('click', this.handleBackdropClick.bind(this));
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  removeEventListeners() {
    const { dialog } = this.refs;
    dialog.removeEventListener('click', this.handleBackdropClick.bind(this));
    document.removeEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleBackdropClick(event) {
    if (event.target === this.refs.dialog) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (!this.refs.countryOverlay.hasAttribute('hidden')) {
        event.preventDefault();
        this.closeCountryList();
      } else if (!this.refs.languageOverlay.hasAttribute('hidden')) {
        event.preventDefault();
        this.closeLanguageList();
      } else {
        event.preventDefault();
        this.close();
      }
    }
  }
}

// Global function to open language drawer
window.openLanguageDrawer = function(drawerId, triggerElement) {
  const event = new CustomEvent('open-language-drawer', {
    detail: { drawerId, triggerElement }
  });
  document.dispatchEvent(event);
};

if (!customElements.get('language-selector-drawer')) {
  customElements.define('language-selector-drawer', LanguageSelectorDrawer);
}
